import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { tenantFeaturesService, type TenantFeatureDto } from '@/services/tenantFeatures';
import { tenantService, type TenantDto } from '@/services/tenants';
import { Settings, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const SuperadminTenantFeaturesPage: React.FC = () => {
  const { toast } = useToast();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId')
  );
  const [selectedTenant, setSelectedTenant] = useState<TenantDto | null>(null);
  const [features, setFeatures] = useState<TenantFeatureDto[]>([]);
  const [loading, setLoading] = useState(false);

  const enabledCount = useMemo(() => features.filter((feature) => feature.enabled).length, [features]);

  useEffect(() => {
    const tenantId = sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId');
    setSelectedTenantId(tenantId);
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return;
    loadTenantAndFeatures(selectedTenantId);
  }, [selectedTenantId]);

  const loadTenantAndFeatures = async (tenantId: string) => {
    try {
      setLoading(true);
      const [tenant, tenantFeatures] = await Promise.all([
        tenantService.getTenant(tenantId),
        tenantFeaturesService.getTenantFeatures(tenantId),
      ]);
      setSelectedTenant(tenant);
      setFeatures(Array.isArray(tenantFeatures) ? tenantFeatures : []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load tenant features',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (featureKey: string, enabled: boolean) => {
    if (!selectedTenantId) return;

    try {
      if (enabled) {
        await tenantFeaturesService.disableFeature(selectedTenantId, featureKey);
      } else {
        await tenantFeaturesService.enableFeature(selectedTenantId, featureKey);
      }
      await loadTenantAndFeatures(selectedTenantId);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update feature',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Tenant Features - Arrangelly Dashboard</title>
      </Helmet>
      <DashboardLayout>
        <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
          <Card className="">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Features
              </CardTitle>
              <CardDescription>
                {selectedTenant
                  ? `${selectedTenant.name}: ${enabledCount}/${features.length} enabled`
                  : 'Select a tenant in Tenants page to manage features'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedTenantId ? (
                <p className="text-gray-400">No tenant selected. Go to Tenants and choose one.</p>
              ) : loading ? (
                <p className="text-gray-400">Loading tenant features...</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-start sm:justify-end">
                    <Button
                      variant="outline"
                      className="border-white/20"
                      onClick={() => selectedTenantId && loadTenantAndFeatures(selectedTenantId)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                  {features.length === 0 ? (
                    <p className="text-gray-400">No features found for this tenant.</p>
                  ) : (
                    features.map((feature) => (
                      <div
                        key={feature.featureKey}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50"
                      >
                        <div>
                          <p className="text-foreground font-medium">{feature.featureName}</p>
                          <p className="text-xs text-gray-400">
                            {feature.featureKey} - {feature.planType.toUpperCase()}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 self-start sm:self-auto">
                          {feature.enabled ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-500" />
                          )}
                          <Button
                            size="sm"
                            variant={feature.enabled ? 'destructive' : 'default'}
                            onClick={() => toggleFeature(feature.featureKey, feature.enabled)}
                          >
                            {feature.enabled ? 'Disable' : 'Enable'}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default SuperadminTenantFeaturesPage;
